import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Check, Loader2 } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const s = useSiteSettings();
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_date: "",
    venue_city: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setMessage("Please fill in name, email, and message.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await axios.post(`${API_BASE}/contact`, form);
      setStatus("success");
      setMessage(res.data.message || "Thanks — we'll be in touch soon.");
      setForm({ name: "", email: "", event_date: "", venue_city: "", message: "" });
      setTimeout(() => setStatus("idle"), 6000);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#DC2626]" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              Booking & Press
            </span>
          </div>
          <h2 className="font-anton uppercase text-white text-5xl md:text-6xl leading-[0.9] mb-8">
            Book <span className="text-[#B91C1C]">The Band</span>
          </h2>
          <p className="text-white/70 font-manrope text-lg mb-10 max-w-md">
            {s.booking_blurb || "Private parties, festivals, fairs, weddings, concerts."}
          </p>

          <div className="space-y-5">
            {(() => {
              const c1 = {
                icon: Mail,
                label: s.contact_1_label || "Booking",
                value: s.booking_email || "booking@randylovelessband.com",
                href: `mailto:${s.booking_email || "booking@randylovelessband.com"}`,
                testid: "contact-1-email",
              };
              const p1 = s.phone
                ? {
                    icon: Phone,
                    label: s.contact_1_label ? `${s.contact_1_label} · Phone` : "Phone",
                    value: s.phone,
                    href: `tel:${s.phone.replace(/[^\d+]/g, "")}`,
                    testid: "contact-1-phone",
                  }
                : null;
              const c2 = s.booking_email_2
                ? {
                    icon: Mail,
                    label: s.contact_2_label || "Booking (2)",
                    value: s.booking_email_2,
                    href: `mailto:${s.booking_email_2}`,
                    testid: "contact-2-email",
                  }
                : null;
              const p2 = s.phone_2
                ? {
                    icon: Phone,
                    label: s.contact_2_label ? `${s.contact_2_label} · Phone` : "Phone (2)",
                    value: s.phone_2,
                    href: `tel:${s.phone_2.replace(/[^\d+]/g, "")}`,
                    testid: "contact-2-phone",
                  }
                : null;
              const loc = {
                icon: MapPin,
                label: "Based In",
                value: s.location || "Kalispell, Montana",
                href: null,
                testid: "contact-location",
              };
              return [c1, p1, c2, p2, loc].filter(Boolean);
            })().map((c) => (
              <div
                key={c.testid}
                data-testid={c.testid}
                className="flex items-start gap-4 group"
              >
                <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 p-3 group-hover:bg-[#B91C1C] transition-colors">
                  <c.icon size={18} className="text-[#DC2626] group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="font-oswald uppercase tracking-[0.2em] text-xs text-white/50">
                    {c.label}
                  </div>
                  {c.href ? (
                    <a
                      href={c.href}
                      className="font-manrope text-white text-base md:text-lg hover:text-[#DC2626] transition-colors break-all"
                    >
                      {c.value}
                    </a>
                  ) : (
                    <div className="font-manrope text-white text-base md:text-lg">{c.value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="booking-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-[#0F0F0F] border border-white/10 p-8 md:p-10"
        >
          <h3 className="font-anton uppercase text-2xl text-white mb-6">
            Send a Booking Inquiry
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Your Name"
              id="booking-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
            />
            <Field
              label="Email"
              id="booking-email"
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
            <Field
              label="Event Date"
              id="booking-date"
              type="date"
              value={form.event_date}
              onChange={(v) => setForm({ ...form, event_date: v })}
            />
            <Field
              label="Venue / City"
              id="booking-venue"
              value={form.venue_city}
              onChange={(v) => setForm({ ...form, venue_city: v })}
            />
          </div>
          <div className="mt-5">
            <label className="font-oswald uppercase tracking-[0.2em] text-xs text-white/60 block mb-2">
              Tell us about the gig
            </label>
            <textarea
              data-testid="booking-message"
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Type of event, expected crowd, stage info, budget, etc."
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#B91C1C] outline-none text-white placeholder-white/30 py-3 font-manrope transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={status === "loading"}
            data-testid="booking-submit"
            className="mt-8 inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-sm bg-[#B91C1C] hover:bg-[#991B1B] disabled:opacity-60 text-white px-8 py-4 brutal-shadow"
          >
            {status === "loading" ? (
              <><Loader2 size={16} className="animate-spin" /> Sending...</>
            ) : status === "success" ? (
              <><Check size={16} /> Sent</>
            ) : (
              "Send Inquiry"
            )}
          </button>
          {message && (
            <p
              data-testid="booking-form-message"
              className={`mt-4 font-manrope text-sm ${
                status === "success" ? "text-green-400" : "text-red-400"
              }`}
            >
              {message}
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, id, type = "text", value, onChange }) => (
  <div>
    <label
      htmlFor={id}
      className="font-oswald uppercase tracking-[0.2em] text-xs text-white/60 block mb-2"
    >
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={id}
      className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#B91C1C] outline-none text-white placeholder-white/30 py-3 font-manrope transition-colors"
    />
  </div>
);
